import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Sparkles, AlertTriangle, Check } from 'lucide-react';

export default function PetCarePage() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 md:py-16 space-y-12">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-xs text-gray-550 mb-4 overflow-x-auto whitespace-nowrap">
        <Link to="/" className="hover:text-primary transition-colors font-medium">Home</Link>
        <ChevronRight size={12} className="text-gray-400" />
        <span className="text-primary font-bold">Pet Care Guide</span>
      </nav>

      {/* Hero Header */}
      <div className="text-center space-y-4 max-w-2xl mx-auto">
        <div className="w-12 h-12 bg-white border border-accent/25 rounded-full flex items-center justify-center mx-auto text-accent shadow-sm">
          <Sparkles size={20} />
        </div>
        <h1 className="text-3xl md:text-4.5xl font-serif font-extrabold text-primary tracking-tight">Pet Care & Nutrition Guidelines</h1>
        <div className="w-10 h-0.5 bg-accent/40 mx-auto" />
        <p className="text-gray-500 text-xs md:text-sm font-semibold leading-relaxed">
          Adopting a companion is a lifetime promise. Read these veterinarian-approved guidelines from Royal Pet Store to keep your dogs, cats, and birds active, healthy, and happy.
        </p>
      </div>

      {/* Core Guidelines Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* Grooming */}
        <div className="bg-white rounded-[32px] border border-gray-100 p-6 md:p-8 shadow-premium space-y-6">
          <h2 className="text-sm font-serif font-extrabold text-primary uppercase tracking-wider flex items-center gap-2 border-b border-gray-50 pb-3">
            <span>🧹</span> Grooming & Coat Care
          </h2>
          <ul className="space-y-4 text-xs font-semibold text-gray-650 leading-relaxed">
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Weekly Fur Brushing</strong>
              <p className="text-gray-500">Brush coat 2-3 times weekly. It removes loose dander, blocks matting, and stimulates natural skin hydration oils.</p>
            </li>
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Bathing Cycle</strong>
              <p className="text-gray-500">Wash dogs every 4-6 weeks with pet-formulated shampoo. Cats groom themselves carefully and require baths only if exceptionally soiled.</p>
            </li>
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Nail Trimming</strong>
              <p className="text-gray-500">Clip claws every 3-4 weeks. Click sounds on tile/hardwood floor indicate excessive lengths which strain pet joints.</p>
            </li>
          </ul>
        </div>

        {/* Nutrition */}
        <div className="bg-white rounded-[32px] border border-gray-100 p-6 md:p-8 shadow-premium space-y-6">
          <h2 className="text-sm font-serif font-extrabold text-primary uppercase tracking-wider flex items-center gap-2 border-b border-gray-50 pb-3">
            <span>🥩</span> Diet & Hydration
          </h2>
          <ul className="space-y-4 text-xs font-semibold text-gray-650 leading-relaxed">
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Age-Appropriate Feed</strong>
              <p className="text-gray-500">Use puppy/kitten food during first 12 months growth. Transition formulas over 7-10 days to protect digestive microbiomes.</p>
            </li>
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Portions Tracking</strong>
              <p className="text-gray-500">Measure feeding bowls by weight guidelines on packaging. Avoid constant free-feeding which causes weight and joints stress.</p>
            </li>
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Pure Drinking Water</strong>
              <p className="text-gray-500">Ensure fresh hydration bowls at all times. Cats prefer drinking from water fountains as running water stimulates thirst triggers.</p>
            </li>
          </ul>
        </div>

        {/* Health Care */}
        <div className="bg-white rounded-[32px] border border-gray-100 p-6 md:p-8 shadow-premium space-y-6">
          <h2 className="text-sm font-serif font-extrabold text-primary uppercase tracking-wider flex items-center gap-2 border-b border-gray-50 pb-3">
            <span>🏥</span> Veterinary Clearance & Health
          </h2>
          <ul className="space-y-4 text-xs font-semibold text-gray-650 leading-relaxed">
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Vaccination Cycles</strong>
              <p className="text-gray-500">Confirm core vaccinations (DHLPP, FVRCP, and Rabies) are updated. Retain physical medical records for transit clearances.</p>
            </li>
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Anti-Parasite Controls</strong>
              <p className="text-gray-500">Administer monthly flea/tick solutions and regular dewormers. This protects family hygiene and companion comfort levels.</p>
            </li>
          </ul>
        </div>

        {/* Physical Exercise */}
        <div className="bg-white rounded-[32px] border border-gray-100 p-6 md:p-8 shadow-premium space-y-6">
          <h2 className="text-sm font-serif font-extrabold text-primary uppercase tracking-wider flex items-center gap-2 border-b border-gray-50 pb-3">
            <span>🎾</span> Mental & Physical Exercise
          </h2>
          <ul className="space-y-4 text-xs font-semibold text-gray-650 leading-relaxed">
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Daily Activity Needs</strong>
              <p className="text-gray-500">Dogs need at least 30-60 minutes walking/playtime daily to vent energy and block destructive chewing habits.</p>
            </li>
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Active Toys</strong>
              <p className="text-gray-500">Engage cats with feather wands, laser pointers, and food puzzles to challenge hunting instincts.</p>
            </li>
            <li className="space-y-1">
              <strong className="text-primary font-bold uppercase tracking-wider text-[10px] flex items-center gap-1.5"><Check size={12} className="text-accent" /> Controlled Socialization</strong>
              <p className="text-gray-500">Introduce pets to friendly animals and new locations after vaccines are complete to build companion confidence.</p>
            </li>
          </ul>
        </div>

      </div>

      {/* Cautionary Toxic Foods Banner */}
      <div className="bg-red-50/50 border border-red-200/60 rounded-[32px] p-6 md:p-8 flex flex-col sm:flex-row gap-5 shadow-sm">
        <div className="w-12 h-12 rounded-2xl bg-red-500/10 text-red-650 flex items-center justify-center flex-shrink-0 shadow-sm">
          <AlertTriangle size={24} />
        </div>
        <div className="space-y-2">
          <h3 className="font-serif font-extrabold text-red-950 text-sm uppercase tracking-wider">Critical Toxicity Alert (Avoid These Foods)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs font-semibold text-red-800/95 leading-relaxed">
            <div>• <strong>No Chocolates or Caffeine:</strong> Causes severe heart palpitations, muscle tremors, and nervous disruptions.</div>
            <div>• <strong>No Onions or Garlic:</strong> Promotes red blood cells damage, causing acute anemia or breathing stress.</div>
            <div>• <strong>No Grapes or Raisins:</strong> Highly toxic, even small quantities trigger acute kidney disruptions.</div>
            <div>• <strong>No Cooked Bones:</strong> Splinters easily in throat, causing severe internal stomach punctures.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
